import { html, css, LitElement } from 'lit-element';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { clone, cloneDeep } from 'lodash-es';
import { library, dom } from "@fortawesome/fontawesome-svg-core";   
import { a11yStyles, escapeXml } from './a11y.js';
import { t, Localized } from './i18n.js';

export class IntrivityLikertReport extends Localized(LitElement) {

    static get properties() {
        return {
            you: { type: Number },
            color: { type: String },
            colorhue: { type: Number },
            colorsat: { type: Number },
            colorlight: { type: Number },
            groups: { type: Array },
            max: { type: Number },
            min: { type: Number },
            left_text: { type: String },
            right_text: { type: String },
            size_max: { type: Number }
        };
    }

    constructor() {
        super();
        this.max = 16;
        this.min = 4;
        this.size_max = 5;
        this.generateSVG = this.generateSVG.bind(this);
        this.getWeight = this.getWeight.bind(this);
        this.getWeightSz = this.getWeightSz.bind(this);
        this.groups = [];
    }

    connectedCallback() {
        super.connectedCallback();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }
    
    // Size of each group marker should be scaled based on max size of score
    // the size of the score and the max pixel size for the component.
    getWeight(g) {
        if (g.max > 3) { this.size_max = g.max; } else { this.size_max = 3; }
        let x =  Math.trunc((( this.max ) * g.size) / this.size_max) + this.min;
        return x;
    }
    // this.size_max
    getWeightSz(sz) {
          
            let x =  Math.trunc((( this.max ) * sz) / this.size_max) + this.min + 1;
            return x;
    }

    // Text equivalent for the chart. The scale runs 1-4 left to right; the
    // shaded circles show how many team members landed on each point.
    describe() {
        const scale = (this.left_text && this.right_text)
            ? t('likert.scaleLabelled', { left: this.left_text, right: this.right_text })
            : t('likert.scale');
        const you = this.you ? ' ' + t('likert.yourScore', { score: this.you }) : '';
        let team = '';
        if (this.groups && this.groups.length > 0) {
            const parts = this.groups
                .filter((g) => g && g.size > 0)
                .map((g) => t('likert.teamAt', { count: g.size, score: g.score }));
            if (parts.length) team = ' ' + t('likert.teamMembers', { list: parts.join(', ') });
        }
        return `${scale}${you}${team}`;
    }

    generateSVG() {
        //if (this.size_max < 5) this.size_max = 5;
        let svg = '<svg style="width: 100%; height: 200;" xmlns="http://www.w3.org/2000/svg" version="1.1"'
            + ' role="img" aria-label="' + escapeXml(this.describe()) + '">';
        svg += '<rect x="6%" y="55%" stroke="white" stroke-width="3" width="22%" height="24px" transform="translate(0,-12)" fill="hsl(' + this.colorhue + ',' + this.colorsat + '%,' + this.colorlight + '%)"/>';
        svg += '<rect x="28%" y="55%" stroke="white" stroke-width="3" width="22%" height="24px" transform="translate(0,-12)" fill="hsl(' + this.colorhue + ',' + this.colorsat + '%,' + this.colorlight + '%)"/>';
        svg += '<rect x="50%" y="55%" stroke="white" stroke-width="3" width="22%" height="24px" transform="translate(0,-12)" fill="hsl(' + this.colorhue + ',' + this.colorsat + '%,' + this.colorlight + '%)"/>';
        svg += '<rect x="72%" y="55%" stroke="white" stroke-width="3" width="22%" height="24px" transform="translate(0,-12)" fill="hsl(' + this.colorhue + ',' + this.colorsat + '%,' + this.colorlight + '%)"/>';
        if (this.left_text) {
            svg += '<text x="4%" y="25" dominant-baseline="start" text-anchor="start" fill="black">';
            svg += this.left_text;
            svg += '</text>';
        } 
        if (this.right_text) {
            svg += '<text x="96%" y="25" dominant-baseline="end" text-anchor="end" fill="black">';
            svg += this.right_text;
            svg += '</text>';
        }
        
        // Add in groups
        this.groups.map((group)=>{ 
                svg += '<circle cx="'  + (((group.score-1)*22)+6) + '%" cy="55%" r="' + this.getWeight(group) + '" fill-opacity="30%" />'; 
            });
        // Add in you
        if (this.you) {
            svg += '<rect x="' + (((this.you-1)*22)+6) + '%" y="30%" width="4px" height="25%" transform="translate(-2,12)" fill="#595959"/>';
            svg += '<text x="' + (((this.you-1)*22)+6) + '%" y="65" transform="translate(-2,-15)" dominant-baseline="middle" text-anchor="middle" fill="#595959">';
            svg += escapeXml(t('likert.you')) + '<tspan x="' + (((this.you-1)*22)+6) + '%" y="80">' + this.you + '</tspan></text>';
        }
        svg += '<svg viewBox="0 0 80 60" x="80%" y="55" width="80">';
        // Add in group legend
        if (this.size_max == 1) {
            svg += '<circle cx="39%" cy="50%" r="' + this.getWeightSz(1) + '" fill-opacity="30%" />';
            svg += '<text font-size="small" x="39%" y="50%" transform="translate(-1,2)" dominant-baseline="middle" text-anchor="middle" fill="black">1</text>';            
        } else {
            svg += '<circle cx="20%" cy="50%" r="' + this.getWeightSz(1) + '" fill-opacity="30%" />';
            svg += '<text font-size="small" x="20%" y="50%" transform="translate(-1,2)" dominant-baseline="middle" text-anchor="middle" fill="black">1</text>';
        }
        if (this.size_max > 2) {
            svg += '<circle cx="39%" cy="50%" r="' + this.getWeightSz(Math.round(this.size_max/2)) + '" fill-opacity="30%" />';
        }
        if (this.size_max > 1) {
            svg += '<circle cx="65%" cy="50%" r="' + this.getWeightSz(this.size_max) + '" fill-opacity="30%" />';   
            svg += '<text font-size="small" x="65%" y="50%" transform="translate(5,2)" dominant-baseline="middle" text-anchor="middle" fill="black">' + this.size_max + '</text>'; 
        }
        svg += '</svg>';
        svg += '</svg>';
        
        return svg;
    }
    
    static get styles() {
        return [a11yStyles];
    }

    render() {
        return html`
            ${ unsafeHTML(this.generateSVG()) }        
        `
    } 
 
}
if (!customElements.get('intrivity-likert-report')) customElements.define('intrivity-likert-report', IntrivityLikertReport);
